import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion'; // Import AnimatePresence
import { CurrencyDollarIcon, ChartBarIcon, ScaleIcon, CogIcon, CircleStackIcon, ArrowTrendingUpIcon } from '@heroicons/react/24/solid'; // Using solid icons for better visibility
import logo from '../../assets/images/logo-nowords.png'; // Adjust path if your logo is elsewhere

interface BlendingSpinnerProps {
  statusMessage?: string | null; // Changed from string | undefined
}

const BlendingSpinner: React.FC<BlendingSpinnerProps> = ({ statusMessage }) => {
  const iconSize = 'w-8 h-8'; // Size for orbiting icons

  // Memoize the icons array to prevent recreation on every render
  const icons = useMemo(() => [
    { Icon: CurrencyDollarIcon, color: 'text-green-500', initialAngle: 0, orbitRadius: 120 }, // Increased from 80
    { Icon: ChartBarIcon, color: 'text-blue-500', initialAngle: 90, orbitRadius: 135 },      // Increased from 90
    { Icon: ScaleIcon, color: 'text-yellow-500', initialAngle: 180, orbitRadius: 120 },    // Increased from 80
    { Icon: CogIcon, color: 'text-purple-500', initialAngle: 270, orbitRadius: 135 },     // Increased from 90
    { Icon: CircleStackIcon, color: 'text-pink-500', initialAngle: 45, orbitRadius: 130 },   // Increased from 85
    { Icon: ArrowTrendingUpIcon, color: 'text-teal-500', initialAngle: 135, orbitRadius: 115 },// Increased from 75
  ], []);

  // Memoize animation properties for each icon to prevent recalculation
  const iconAnimations = useMemo(() => {
    return icons.map(({ initialAngle, orbitRadius }, index) => {
      const r = orbitRadius;
      
      // Blender effect: icons spiral inward (pulled toward center) and fling outward
      // Using smooth radius variation for continuous blending motion
      const radiusVariation = [
        [1.0, 0.5, 0.3, 0.6, 1.1, 1.3, 1.0],   // Icon 0 - moderate blending
        [1.0, 0.4, 0.25, 0.5, 1.15, 1.35, 1.0], // Icon 1 - deeper pull
        [1.0, 0.55, 0.35, 0.65, 1.05, 1.25, 1.0], // Icon 2 - gentler blend
        [1.0, 0.35, 0.2, 0.45, 1.2, 1.4, 1.0],   // Icon 3 - aggressive blend
        [1.0, 0.5, 0.3, 0.6, 1.1, 1.3, 1.0],   // Icon 4 - moderate
        [1.0, 0.45, 0.28, 0.55, 1.12, 1.32, 1.0], // Icon 5 - varied
      ];
      const rKeyframes = radiusVariation[index % radiusVariation.length].map(multiplier => r * multiplier);

      // Continuous rotation for seamless orbiting (full 360° loop)
      const numSteps = 7;
      const angleSteps = Array.from({ length: numSteps }, (_, i) => (360 / (numSteps - 1)) * i);
      
      // Mix clockwise and counterclockwise for chaotic blender effect
      const rotationDirections = [1, -1, 1, -1, 1, -1];
      const direction = rotationDirections[index % rotationDirections.length];

      // Stagger durations to create chaotic yet continuous blending motion
      const durations = [4.5, 5.2, 4.8, 5.5, 4.2, 5.0];
      const duration = durations[index % durations.length];

      return {
        x: angleSteps.map((step, i) => 
          Math.cos((initialAngle + step * direction) * (Math.PI / 180)) * rKeyframes[i]
        ),
        y: angleSteps.map((step, i) => 
          Math.sin((initialAngle + step * direction) * (Math.PI / 180)) * rKeyframes[i]
        ),
        rotate: [0, 180, 360, 540, 720, 900, 1080], // Continuous rotation
        scale: [1.0, 1.3, 0.7, 1.2, 0.8, 1.1, 1.0], // Seamless scale loop
        opacity: [0.8, 0.9, 0.6, 0.95, 0.65, 0.85, 0.8], // Seamless opacity loop
        transition: {
          duration: duration,
          repeat: Infinity,
          ease: 'linear', // Changed to linear for continuous motion
          repeatType: 'loop' as const,
        }
      };
    });
  }, [icons]);  return (
    <motion.div 
      key="blending-spinner-main" // Stable key for the main container
      className="fixed inset-0 z-[999999] flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 overflow-hidden loading-transition"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      style={{ pointerEvents: 'none' }} // Prevent any interaction issues during transitions
    >
      <motion.div
        key="blending-spinner-center" // Stable key for the center container
        className="relative flex items-center justify-center"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        {/* Spinning Logo */}
        <motion.img
          key="blending-spinner-logo" // Stable key for the logo
          src={logo}
          alt="Blending..."
          className="w-32 h-32 md:w-40 md:h-40" // Adjust size as needed
          style={{ mixBlendMode: 'screen' }}
          animate={{
            rotate: 720, // Two full rotations per cycle (matches original speed)
            scale: [1.0, 1.15, 0.9, 1.2, 0.95, 1.0], // Perfect loop: starts 1.0, ends 1.0
            opacity: [0.7, 1.0, 0.6, 1.0, 0.7, 0.7], // Perfect loop: starts 0.7, ends 0.7
          }}
          transition={{
            rotate: { duration: 4.4, ease: 'linear', repeat: Infinity },
            scale: { duration: 3.2, ease: 'linear', repeat: Infinity },
            opacity: { duration: 3.2, ease: 'linear', repeat: Infinity },
          }}
        />{/* Orbiting Icons */}
        {icons.map(({ Icon, color }, index) => {
          const animation = iconAnimations[index];
          
          return (
            <motion.div
              key={`orbit-icon-${index}`} // Stable key that doesn't change with status
              className={`absolute ${iconSize} ${color}`}
              style={{ originX: '50%', originY: '50%' }}
              animate={{
                x: animation.x,
                y: animation.y,
                rotate: animation.rotate,
                scale: animation.scale,
                opacity: animation.opacity,
              }}
              transition={animation.transition}
            >
              <Icon />
            </motion.div>
          );
        })}
      </motion.div>      {/* "Blending..." Text */}
      <motion.p
        key="blending-spinner-title" // Stable key for the title
        className="mt-8 text-2xl md:text-3xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-teal-400 via-cyan-400 to-sky-500"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.8, ease: "easeOut" }}
      >
        Bot Blending...
      </motion.p>
      <motion.div
        key="blending-spinner-underline" // Stable key for the underline
        className="mt-2 h-1 w-32 bg-gradient-to-r from-teal-400 via-cyan-400 to-sky-500 rounded-full"
        initial={{ scaleX: 0, opacity: 0 }}
        animate={{ scaleX: 1, opacity: 1 }}
        transition={{ delay: 0.7, duration: 1, ease: "easeOut" }}
      />
      
      {/* Status Message Container - Stable container for text transitions */}
      <div className="mt-4 h-8 flex items-center justify-center"> {/* Fixed height container */}
        <AnimatePresence mode="wait">
          {statusMessage && (
            <motion.p
              key={statusMessage} // Key change triggers animation
              className="text-lg md:text-xl text-gray-300 text-center"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              {statusMessage}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default BlendingSpinner;
